import { App, CreateProfileProps, ProfileData, UserType } from '../../clients';

export type AmsAppAccessFieldValues = {
  [App.AMS]: boolean;
  amsRole?: string;
};

export type EnrolmentPlannerAppAccessFieldValues = {
  [App.EnrolmentPlanner]: boolean;
  enrolmentPlannerRole?: string;
};

export type AddProfileFieldValues = {
  email: string;
  givenName: string;
  familyName: string;
  userType: string;
} & AmsAppAccessFieldValues &
  EnrolmentPlannerAppAccessFieldValues;

interface UseProfileFormProps {
  profileDataToBeEdited?: ProfileData;
  currentSessionUser: ProfileData;
  onSubmit: (profileData: CreateProfileProps | Partial<CreateProfileProps>) => void;
}

interface UseProfileFormReturn {
  initialValues: AddProfileFieldValues;
  handleSubmit: (formValue: AddProfileFieldValues) => void;
}

export function useProfileForm({
  profileDataToBeEdited,
  currentSessionUser,
  onSubmit,
}: UseProfileFormProps): UseProfileFormReturn {
  const initialValues: AddProfileFieldValues = {
    email: profileDataToBeEdited?.email || '',
    givenName: profileDataToBeEdited?.givenName || '',
    familyName: profileDataToBeEdited?.familyName || '',
    userType: profileDataToBeEdited?.userType || '',
    [App.AMS]: profileDataToBeEdited?.apps.includes(App.AMS) || false,
    [App.EnrolmentPlanner]: profileDataToBeEdited?.apps.includes(App.EnrolmentPlanner) || false,
  };

  const handleSubmit = (formValue: AddProfileFieldValues) => {
    // Create an array to hold the apps that are enabled
    const appsEnabled = [];
    if (formValue[App.AMS]) {
      appsEnabled.push(App.AMS);
    }
    if (formValue[App.EnrolmentPlanner]) {
      appsEnabled.push(App.EnrolmentPlanner);
    }

    // Transform form data for submission
    const fullSubmissionData: CreateProfileProps = {
      email: formValue.email,
      givenName: formValue.givenName,
      familyName: formValue.familyName,
      userType: formValue.userType as UserType,
      apps: appsEnabled,
    };

    // For editing existing profiles, only send changed fields
    if (profileDataToBeEdited) {
      const changedFields: Partial<CreateProfileProps> = {};

      // Check each field for changes
      if (formValue.email !== initialValues.email) {
        changedFields.email = formValue.email;
      }
      if (formValue.givenName !== initialValues.givenName) {
        changedFields.givenName = formValue.givenName;
      }
      if (formValue.familyName !== initialValues.familyName) {
        changedFields.familyName = formValue.familyName;
      }
      if (formValue.userType !== initialValues.userType) {
        changedFields.userType = formValue.userType as UserType;
      }

      // Check if apps have changed
      const initialApps = [
        ...(initialValues[App.AMS] ? [App.AMS] : []),
        ...(initialValues[App.EnrolmentPlanner] ? [App.EnrolmentPlanner] : []),
      ];
      const currentApps = appsEnabled;

      // Compare arrays to see if apps changed
      const appsChanged =
        initialApps.length !== currentApps.length || !initialApps.every((app) => currentApps.includes(app));

      if (appsChanged) {
        changedFields.apps = appsEnabled;
      }

      // If user is staff, only allow editing name fields
      if (currentSessionUser.userType === UserType.staff) {
        const staffChangedFields: Partial<CreateProfileProps> = {};
        if (changedFields.givenName !== undefined) {
          staffChangedFields.givenName = changedFields.givenName;
        }
        if (changedFields.familyName !== undefined) {
          staffChangedFields.familyName = changedFields.familyName;
        }
        onSubmit(staffChangedFields);
      } else {
        onSubmit(changedFields);
      }
    } else {
      // For creating new profiles, send full data
      if (currentSessionUser.userType === UserType.staff) {
        onSubmit({
          email: fullSubmissionData.email,
          givenName: fullSubmissionData.givenName,
          familyName: fullSubmissionData.familyName,
          userType: fullSubmissionData.userType,
          apps: fullSubmissionData.apps,
        });
      } else {
        onSubmit(fullSubmissionData);
      }
    }
  };

  return {
    initialValues,
    handleSubmit,
  };
}
